<template>
  <div
    v-if="!settings.hideLabel"
    class="color-select"
  >
    <div class="label">{{ settings.label }}</div>
    <el-select
      :model-value="modelValue"
      :placeholder="settings.placeholder || 'Ingresar Color'"
      filterable
      :clearable="settings.clearable !== false"
      remote
      :remote-method="remoteSearch"
      :loading="loading"
      :size="settings.size || 'default'"
      :popper-class="settings.popperClass || 'color-select-dropdown'"
      :teleported="settings.teleported !== false"
      :disabled="!!settings.disabled"
      :style="{ width: settings.width || '100%' }"
      @update:model-value="onChange"
    >
      <template #prefix>
        <span
          v-if="selectedObj"
          class="chip chip--inline"
          :style="{ backgroundColor: selectedObj.Hex || '#ffffff' }"
          :title="selectedObj.Hex || ''"
        />
      </template>

      <el-option
        v-for="opt in options"
        :key="opt.Codigo"
        :label="opt.Descripcion || '—'"
        :value="opt.Codigo"
      >
        <div class="opt">
          <div class="desc">
            <div>{{ opt.Descripcion || '—' }}</div>
            <small
              v-if="opt.Pantone"
              class="muted"
              >Pantone: {{ opt.Pantone }}</small
            >
          </div>
          <span
            class="chip"
            :style="{ backgroundColor: opt.Hex || '#ffffff' }"
            :title="opt.Hex || ''"
          />
        </div>
      </el-option>
    </el-select>
  </div>

  <el-select
    v-else
    :model-value="modelValue"
    :placeholder="settings.placeholder || 'Ingresar Color'"
    filterable
    :clearable="settings.clearable !== false"
    remote
    :remote-method="remoteSearch"
    :loading="loading"
    :size="settings.size || 'default'"
    :popper-class="settings.popperClass || 'color-select-dropdown'"
    :teleported="settings.teleported !== false"
    :disabled="!!settings.disabled"
    :style="{ width: settings.width || '100%' }"
    @update:model-value="onChange"
  >
    <template #prefix>
      <span
        v-if="selectedObj"
        class="chip chip--inline"
        :style="{ backgroundColor: selectedObj.Hex || '#ffffff' }"
        :title="selectedObj.Hex || ''"
      />
    </template>

    <el-option
      v-for="opt in options"
      :key="opt.Codigo"
      :label="opt.Descripcion || '—'"
      :value="opt.Codigo"
    >
      <div class="opt">
        <div class="desc">
          <div>{{ opt.Descripcion || '—' }}</div>
          <small
            v-if="opt.Pantone"
            class="muted"
            >Pantone: {{ opt.Pantone }}</small
          >
        </div>
        <span
          class="chip"
          :style="{ backgroundColor: opt.Hex || '#ffffff' }"
          :title="opt.Hex || ''"
        />
      </div>
    </el-option>
  </el-select>
</template>

<script setup>
import {
  computed,
  defineProps,
  defineEmits,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useColoresStore } from '@/views/colores/stores/colores_store.js';

const props = defineProps({
  modelValue: { type: [Number, null], default: null },
  settings: {
    type: Object,
    default: () => ({
      label: 'Color',
      hideLabel: false,
      placeholder: 'Ingresar Color',
      minQuery: 2,
      limit: 20,
      clearable: true,
      size: 'default',
      teleported: true,
      popperClass: 'color-select-dropdown',
      disabled: false,
      width: '100%',
    }),
  },
  fetchers: {
    type: Object,
    default: null,
  },
  preload: {
    type: Object,
    default: null,
  },
});

const emits = defineEmits(['update:modelValue', 'change', 'select-object']);

const coloresStore = useColoresStore();
const cache = ref(new Map());
const options = ref([]);
const loading = ref(false);
const queryStr = ref('');

const normalizeColor = (c) => ({
  Codigo: Number(c?.Codigo),
  Descripcion: c?.Descripcion || '',
  Hex: c?.Hex || null,
  Pantone: c?.Pantone || null,
});

const searchList = async (q) => {
  const impl = props.fetchers?.searchList || coloresStore.searchColoresHTTP;
  const data = await impl(q);
  return Array.isArray(data) ? data.map(normalizeColor) : [];
};
const getById = async (id) => {
  if (id === null || !Number.isFinite(Number(id)) || Number(id) <= 0) return null;
  const impl = props.fetchers?.getById || coloresStore.getColorByCodigoHTTP;
  const c = await impl(Number(id));
  return c ? normalizeColor(c) : null;
};

const upsertCache = (list = []) => {
  for (const c of list) {
    if (c && c.Codigo !== null) cache.value.set(Number(c.Codigo), normalizeColor(c));
  }
};
const ensureVisible = (list = []) => {
  const seen = new Set();
  const out = [];
  for (const c of list) {
    if (!c || c.Codigo === null) continue;
    const code = Number(c.Codigo);
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(cache.value.get(code) || normalizeColor(c));
  }
  options.value = out;
};
const selectedObj = computed(() => {
  const code = Number(props.modelValue);
  if (!Number.isFinite(code) || code <= 0) return null;
  return cache.value.get(code) || null;
});

let debounceTimer = null;
const runDebounce = (fn, delay = 250) => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fn, delay);
};

const remoteSearch = (q) => {
  queryStr.value = (q || '').trim();
  const min = Number(props.settings.minQuery || 2);
  runDebounce(async () => {
    const s = queryStr.value;
    if (!s || s.length < min) {
      if (selectedObj.value) ensureVisible([selectedObj.value]);
      else options.value = [];
      return;
    }

    loading.value = true;
    try {
      const list = await searchList(s);
      const sLower = s.toLowerCase();
      const ranked = list
        .slice()
        .sort((a, b) => {
          const A = (a.Descripcion || '').toLowerCase();
          const B = (b.Descripcion || '').toLowerCase();
          const aw = A.startsWith(sLower) ? 0 : 1;
          const bw = B.startsWith(sLower) ? 0 : 1;
          const Ap = (a.Pantone || '').toLowerCase().startsWith(sLower) ? -0.5 : 0;
          const Bp = (b.Pantone || '').toLowerCase().startsWith(sLower) ? -0.5 : 0;
          return aw + Ap - (bw + Bp) || A.localeCompare(B, 'es');
        })
        .slice(0, props.settings.limit || 20);

      upsertCache(ranked);
      ensureVisible(ranked);
    } finally {
      loading.value = false;
    }
  }, 200);
};

const onChange = async (val) => {
  const code = Number(val);
  if (!Number.isFinite(code) || code <= 0) {
    emits('update:modelValue', null);
    emits('change', null);
    emits('select-object', null);
    options.value = [];
    return;
  }

  let c = cache.value.get(code);
  if (!c) {
    c = await getById(code);
    if (c) upsertCache([c]);
  }
  if (c) ensureVisible([c, ...options.value]);

  emits('update:modelValue', code);
  emits('change', code);
  emits('select-object', c || null);
};

const seedSelected = async () => {
  const code = Number(props.modelValue);
  if (!Number.isFinite(code) || code <= 0) {
    options.value = [];
    return;
  }
  if (props.preload && Number(props.preload.Codigo) === code) {
    upsertCache([normalizeColor(props.preload)]);
    ensureVisible([cache.value.get(code)]);
    return;
  }
  if (!cache.value.get(code)) {
    const c = await getById(code);
    if (c) upsertCache([c]);
  }
  if (cache.value.get(code)) ensureVisible([cache.value.get(code)]);
};

onMounted(seedSelected);
watch(() => props.modelValue, seedSelected);
</script>

<style scoped>
.color-select-form-item :deep(.el-form-item__label) {
  font-weight: 600;
}
.opt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.desc {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-overflow: ellipsis;
}
.muted {
  color: #909399;
  font-size: 12px;
}
.chip {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  flex: 0 0 22px;
}
.chip--inline {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  margin-right: 6px;
  border: 1px solid #dcdfe6;
}
.color-select {
  width: 100%;
}
</style>
